import axios, {
	type AxiosRequestConfig,
	type AxiosResponse,
	type AxiosError as OriginalAxiosError,
} from 'axios';

/**
 * Shared Axios instance used by the unified Morval-generated API client.
 *
 * Each environment (browser & server) should call its local `initApiClient`
 * to add interceptors (e.g. auth, CSRF, logging). Those files should import
 * `API_AXIOS_INSTANCE` from:
 *   import { API_AXIOS_INSTANCE } from "@elation/api-client/src/core";
 */
export const API_AXIOS_INSTANCE = axios.create({
	headers: {
		'X-EL8-API-CLIENT': 'morval',
	},
});

/**
 * Prefix all requests with api proxy prefix
 */
API_AXIOS_INSTANCE.interceptors.request.use((config) => {
	if (!config.url) return config;

	return config;
});

/**
 * Wrapper used by all generated endpoints.
 * The wrapper always resolves with the response `data` payload so that both
 * browser and server call-sites have the same ergonomics: the returned value
 * is the parsed JSON body.
 */
export const customInstance = async <T>(
	config: AxiosRequestConfig,
	options?: AxiosRequestConfig,
): Promise<{ response: AxiosResponse<T>; data: T }> => {
	const source = axios.CancelToken.source();
	const response = await API_AXIOS_INSTANCE({
		...config,
		...options,
		cancelToken: source.token,
		withCredentials: true,
	});

	// @ts-expect-error – Morval extends Promise with a `cancel` util
	response.cancel = () => {
		source.cancel('Query was cancelled');
	};

	return {
		response,
		data: response.data,
	};
};

export type AxiosError<T = unknown, D = unknown> = OriginalAxiosError<T, D>;
export type ErrorType<Error> = AxiosError<Error>;
